import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  fetchInspectionReservation, fetchInspection, fetchChecklist, fetchDispute
} from '../../actions/Inspection/inspection';

// Loads what a check-in / check-out screen needs: the reservation header, the inspection of the page type,
// the check-in (for the check-out page: departure mileage and photos), the checklist and the dispute.
// `state.kind` is 'loading' | 'ready' | 'notFound' | 'notLoggedIn' | 'error'.
export function useInspectionData({ reservationId, type }) {
  const dispatch = useDispatch();
  const [state, setState] = useState({ kind: 'loading' });
  const alive = useRef(true);

  const load = useCallback(async () => {
    const [reservation, result, checklist, checkin, dispute] = await Promise.all([
      dispatch(fetchInspectionReservation(reservationId)),
      dispatch(fetchInspection(reservationId, type)),
      type === 'checkin' ? dispatch(fetchChecklist(reservationId)) : Promise.resolve(null),
      type === 'checkout' ? dispatch(fetchInspection(reservationId, 'checkin')) : Promise.resolve(null),
      type === 'checkout' ? dispatch(fetchDispute(reservationId)) : Promise.resolve(null)
    ]);
    if (!alive.current) return null;
    let next;
    if (reservation.status === 'notLoggedIn') next = { kind: 'notLoggedIn' };
    else if (reservation.status === 'failed') next = { kind: 'error' };
    else if (reservation.status === 'notFound' || !reservation.id) next = { kind: 'notFound' };
    else if (result && result.status === 'notLoggedIn') next = { kind: 'notLoggedIn' };
    else if (!result || result.status !== 'success') next = { kind: 'error', errorMessage: result && result.errorMessage, reservation };
    else {
      next = {
        kind: 'ready',
        reservation,
        result,
        inspection: result.inspection || null,
        checklist: checklist && checklist.status === 'success' ? checklist.results || [] : [],
        checkin: checkin && checkin.status === 'success' ? checkin.inspection || null : null,
        dispute: dispute && dispute.status === 'success' ? dispute.dispute || null : null
      };
    }
    setState(next);
    return next;
  }, [dispatch, reservationId, type]);

  useEffect(() => {
    alive.current = true;
    load();
    return () => { alive.current = false; };
  }, [load]);

  return { state, reload: load, setState };
}

export default useInspectionData;
